/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import { Form, Input, Select } from 'formik-antd';
import { Modal, message, Spin, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import errorHandler from '~/Utils/errorHandler';
import Row from '~/components/Row';
import FormControl from '~/components/Form/FormControl';
import { ModalFooter } from '~/components/Modal';
import { decrypt, getSuccessMessageByModule } from '~/Utils/index';

import api from '~/services/api';

import banksOptions from '~/data/banks.json';

const initialValues = {
  description: '',
  bank: '',
  agency: '',
  account_number: '',
};

export default function BankAccountForm({ visible, onClose, bankAccountID }) {
  const { t } = useTranslation();
  const [recordData, setRecordData] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const { permissions } =
    JSON.parse(decrypt(localStorage.getItem('@App:user'))) !== null &&
    JSON.parse(decrypt(localStorage.getItem('@App:user')));

  const handleSave = async (values, { setErrors }) => {
    setLoading(true);
    delete values.isEdit;
    try {
      if (values.id) {
        await api.put(`/bankAccounts/${values.id}`, values);
        message.success(
          getSuccessMessageByModule(t('screens:bankAccounts.titleSingular'), t('messages:successUpdatedFem'))
        );
      } else {
        await api.post('/bankAccounts', values);
        message.success(
          getSuccessMessageByModule(t('screens:bankAccounts.titleSingular'), t('messages:successCreatedFem'))
        );
      }
      onClose();
    } catch (error) {
      setErrors(errorHandler(error));
    }
    setLoading(false);
  };

  const fetchScreenData = async () => {
    setLoading(true);
    try {
      if (!bankAccountID) {
        setRecordData(initialValues);
      } else {
        const response = await api.get(`/bankAccounts/${bankAccountID}`);
        const data = JSON.parse(response.data);
        setRecordData(data);
      }
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (visible) {
      fetchScreenData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const bankAccountchema = Yup.object().shape({
    description: Yup.string().required(),
    bank: Yup.string().required(),
    agency: Yup.string().required(),
    account_number: Yup.string().required(),
  });

  return (
    <Formik
      validateOnBlur={false}
      validateOnChange={false}
      initialValues={recordData}
      enableReinitialize
      onSubmit={handleSave}
      validationSchema={bankAccountchema}
    >
      {({ errors, isSubmitting, submitForm, resetForm }) => (
        <Modal
          title={recordData.id ? `${t('screens:editing')}: ${recordData.description}` : t('screens:bankAccounts.title')}
          onCancel={onClose}
          style={{ maxWidth: '950px' }}
          afterClose={resetForm}
          visible={visible}
          loading={loading || isSubmitting}
          centered
          width="90%"
          footer={
            // eslint-disable-next-line react/jsx-wrap-multilines
            <ModalFooter
              type={permissions && !permissions.includes('@bankAccount/edit') && 'info'}
              onOk={submitForm}
              loading={loading || isSubmitting}
              onCancel={onClose}
            />
          }
        >
          <Spin spinning={loading || isSubmitting}>
            <Form>
              <Row>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <Row>
                    <FormControl
                      cols={{ xs: 12 }}
                      error={errors.description}
                      field="description"
                      label={t('screens:bankAccounts.data.description')}
                      required
                    >
                      <Input name="description" disabled={permissions && !permissions.includes('@bankAccount/edit')} />
                    </FormControl>
                    <FormControl
                      cols={{ xs: 12 }}
                      label={t('screens:bankAccounts.data.bank')}
                      required
                      error={errors.bank}
                      field="bank"
                    >
                      <Select showSearch name="bank" label={t('screens:bankAccounts.data.bank')} required>
                        {banksOptions.map((item) => (
                          <Select.Option key={item.code} value={`${item.code} - ${item.name}`}>
                            {`${item.code} - ${item.name}`}
                          </Select.Option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl
                      cols={{ xs: 12 }}
                      error={errors.agency}
                      field="agency"
                      label={t('screens:bankAccounts.data.agency')}
                      required
                    >
                      <Input name="agency" disabled={permissions && !permissions.includes('@bankAccount/edit')} />
                    </FormControl>
                    <FormControl
                      cols={{ xs: 12 }}
                      error={errors.account_number}
                      field="account_number"
                      label={t('screens:bankAccounts.data.account_number')}
                      required
                    >
                      <Input
                        name="account_number"
                        disabled={permissions && !permissions.includes('@bankAccount/edit')}
                      />
                    </FormControl>
                  </Row>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Modal>
      )}
    </Formik>
  );
}
